import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";

const CONSENT_KEY = "ft_cookie_consent";
const GTM_ID = "GTM-WZRWR7X2";
const GA_ID = "G-NHRD5XLR2J";
function loadScript(src, attrs = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function injectGTM() {
  if (window.__gtm_loaded) return;
  window.__gtm_loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function injectAnalytics() {
  if (window.__ga_loaded) return;
  window.__ga_loaded = true;
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID);
}

function applyConsent(prefs) {
  if (prefs.analytics) {
    injectGTM();
    injectAnalytics();
  }
}

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: true });

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      applyConsent(stored);
    } else {
      setVisible(true);
    }
  }, []);

  const saveAndClose = useCallback((consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setVisible(false);
  }, []);

  const acceptAll = () => saveAndClose({ essential: true, analytics: true });
  const rejectNonEssential = () => saveAndClose({ essential: true, analytics: false });
  const savePreferences = () => saveAndClose(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-2xl shadow-black/40 backdrop-blur-sm">
        <div className="p-5 md:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Cookie size={20} className="text-[hsl(var(--primary))] shrink-0" />
              <h3 className="text-base font-semibold text-[hsl(var(--foreground))]">Cookie Preferences</h3>
            </div>
            <button
              onClick={rejectNonEssential}
              className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label="Close"
              data-testid="cookie-close-btn"
            >
              <X size={18} />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed mb-5">
            We use cookies to analyze traffic via Google Analytics.
            You can accept all, reject non-essential, or customize your preferences.
            Read our <Link to="/privacy-policy" className="text-[hsl(var(--primary))] hover:underline">Privacy Policy</Link> for details.
          </p>

          {/* Manage Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] hover:opacity-80 transition-opacity mb-4"
            data-testid="cookie-manage-btn"
          >
            Manage Preferences {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Preference Details */}
          {showDetails && (
            <div className="space-y-3 mb-5 border-t border-[hsl(var(--border))] pt-4" data-testid="cookie-preferences-panel">
              {/* Essential */}
              <label className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-[hsl(var(--foreground))]">Essential</span>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Required for the site to function</p>
                </div>
                <input type="checkbox" checked disabled className="accent-[hsl(var(--primary))] w-4 h-4" />
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between text-sm cursor-pointer">
                <div>
                  <span className="font-medium text-[hsl(var(--foreground))]">Analytics</span>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Google Analytics and Tag Manager</p>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  className="accent-[hsl(var(--primary))] w-4 h-4 cursor-pointer"
                  data-testid="cookie-analytics-toggle"
                />
              </label>


            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {showDetails ? (
              <Button
                onClick={savePreferences}
                className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
                data-testid="cookie-save-btn"
              >
                Save Preferences
              </Button>
            ) : (
              <>
                <Button
                  onClick={acceptAll}
                  className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
                  data-testid="cookie-accept-btn"
                >
                  Accept All
                </Button>
                <Button
                  onClick={rejectNonEssential}
                  variant="outline"
                  className="flex-1 border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]"
                  data-testid="cookie-reject-btn"
                >
                  Reject Non-Essential
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
